import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { ConfirmationResultModal, Text } from '@percent/lemonade'
import { getAssigneeDisplayName, settleRoleChanges } from '../utils'
import type { RemoveAssigneeModalProps } from './RemoveAssigneeModal.types'

type ModalState = 'confirm' | 'success' | 'failure'

export function RemoveAssigneeModal({
  domainName,
  assignee,
  unassignRole,
  onRemoved,
  onClose,
}: RemoveAssigneeModalProps) {
  const { t } = useTranslation()
  const [modalState, setModalState] = useState<ModalState>('confirm')
  const [isRemoving, setIsRemoving] = useState(false)

  const values = { name: getAssigneeDisplayName(assignee), domainName }
  const bold = { bold: <Text as="span" fontWeight="semiBold" /> }

  const handleRemove = async () => {
    setIsRemoving(true)

    // Removing an assignee removes every role it holds on the domain, one request per role
    const { anySucceeded, allSucceeded } = await settleRoleChanges(
      assignee.roles.map((roleId) => unassignRole(roleId))
    )

    setIsRemoving(false)

    if (anySucceeded) {
      onRemoved()
    }

    setModalState(allSucceeded ? 'success' : 'failure')
  }

  const handleClose = () => {
    if (!isRemoving) {
      onClose()
    }
  }

  return (
    <ConfirmationResultModal
      isOpen
      state={modalState === 'confirm' ? undefined : modalState}
      title={t('modal.removeAssignee.title')}
      primaryButtonText={t('button.remove')}
      loadingText={t('modal.removeAssignee.button.removing')}
      secondaryButtonText={t('button.cancel')}
      onConfirm={handleRemove}
      onClose={handleClose}
      onFailureClose={handleClose}
      loading={isRemoving}
      primaryBtnTestId="remove-assignee-submit-button"
      secondaryBtnTestId="remove-assignee-cancel-button"
      success={{
        title: t('modal.removeAssignee.success.title'),
        description: (
          <Trans
            i18nKey="modal.removeAssignee.success.description"
            values={values}
            components={bold}
          />
        ),
        button: t('button.close'),
      }}
      failure={{
        title: t('modal.removeAssignee.error.title'),
        description: t('modal.removeAssignee.error.description'),
        button: t('button.close'),
      }}
    >
      <Text>
        <Trans
          i18nKey="modal.removeAssignee.description"
          values={values}
          components={bold}
        />
      </Text>
    </ConfirmationResultModal>
  )
}
